'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useClient, useMutation, useQuery } from 'urql';
import { Transfer } from '@/graphql/mutations';
import { copyTextToClipboard, hapticFeedback, popup } from '@tma.js/sdk';
import clsx from 'clsx';
import { Wallets } from '@/graphql/queries';
import type { WalletsQuery, WalletsQueryVariables } from '@/gql/graphql';
import { PAGE_SIZE, useInfiniteList } from '@/hooks/useInfiniteList';
import { formatAmount } from '@/utils/helpers';
import { v4 as uuid4 } from 'uuid';
const leagueBadgeClasses: Record<string, string> = {
  Nestling: 'bg-gradient-to-r from-nestling/50 to-nestling text-foreground',
  Junior: 'bg-gradient-to-r from-junior/50 to-junior text-foreground',
  Master: 'bg-gradient-to-r from-master/50 to-master text-foreground',
  Expert: 'bg-gradient-to-r from-expert/50 to-expert text-foreground',
  Pro: 'bg-gradient-to-r from-pro/50 to-pro text-foreground',
  Plus: 'bg-gradient-to-r from-plus/50 to-plus text-foreground',
  Bronze: 'bg-gradient-to-r from-bronze/50 to-bronze text-foreground',
  Silver: 'bg-gradient-to-r from-silver/50 to-silver text-foreground',
  Gold: 'bg-gradient-to-r from-gold/50 to-gold text-foreground',
  Platinum: 'bg-gradient-to-r from-platinum/50 to-platinum text-foreground',
  Diamond: 'bg-gradient-to-r from-diamond/50 to-diamond text-foreground',
  Premium: 'bg-gradient-to-r from-premium/50 to-premium text-foreground',
  Feathered: 'bg-gradient-to-r from-feathered/50 to-feathered text-foreground'
} as const;
interface Props {
  uuid: string;
  username: string;
  balance: number;
  language?: string | null;
  onClose: () => void;
}
type WalletItem = WalletsQuery['wallets'][number];
export default function TransferModal(t0) {
  const $ = _c(102);
  const {
    uuid,
    username,
    balance,
    language,
    onClose
  } = t0;
  const [isVisible, setIsVisible] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState(1);
  const client = useClient();
  const [transferResult, execTransfer] = useMutation(Transfer);
  const isInvalidAmount = amount <= 0 || amount > balance;
  let t1;
  if ($[0] !== recipient || $[1] !== username || $[2] !== uuid) {
    t1 = recipient.length && (recipient === uuid || recipient.toLowerCase() === username.toLowerCase());
    $[0] = recipient;
    $[1] = username;
    $[2] = uuid;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isOwnRecipient = t1;
  const recipientInputRef = useRef(null);
  const amountInputRef = useRef(null);
  let t2;
  if ($[4] !== debouncedSearch) {
    t2 = offset => ({
      uuid: debouncedSearch || undefined,
      pagination: {
        limit: PAGE_SIZE,
        offset
      }
    }) satisfies WalletsQueryVariables;
    $[4] = debouncedSearch;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== client || $[7] !== t2) {
    t3 = {
      client,
      query: Wallets,
      variablesFn: t2,
      getItems: _temp
    };
    $[6] = client;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    items: wallets,
    isLoadingMore,
    hasMore,
    scrollRef,
    sentinelRef,
    reset
  } = useInfiniteList(t3);
  const t4 = debouncedSearch || undefined;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      limit: PAGE_SIZE,
      offset: 0
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = {
      uuid: t4,
      pagination: t5
    };
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = !debouncedSearch;
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = {
      query: Wallets,
      variables: t6,
      pause: t7
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const [walletsResult] = useQuery(t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = wallet => {
      if (hapticFeedback.isSupported()) {
        hapticFeedback.selectionChanged();
      }
      setRecipient(wallet.bird?.username ?? wallet.uuid);
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleSelectWallet = t9;
  let t10;
  if ($[16] !== amount || $[17] !== execTransfer || $[18] !== isInvalidAmount || $[19] !== isOwnRecipient || $[20] !== language || $[21] !== recipient) {
    t10 = () => {
      if (!isInvalidAmount && !isOwnRecipient) {
        const exec = () => execTransfer({
          transaction: {
            idempotencyKey: uuid4(),
            amount,
            wallet: recipient
          }
        });
        if (popup.isSupported()) {
          popup.show({
            title: "Transaction Confirmation",
            message: `Recipient: ${recipient}\n\nAmount: ${formatAmount(amount, language)} FTH`,
            buttons: [{
              id: "reject",
              type: "destructive",
              text: "Reject"
            }, {
              id: "accept",
              type: "default",
              text: "Accept"
            }]
          }).then(id => {
            if (id === "accept") {
              exec();
            }
          });
        } else {
          exec();
        }
      }
    };
    $[16] = amount;
    $[17] = execTransfer;
    $[18] = isInvalidAmount;
    $[19] = isOwnRecipient;
    $[20] = language;
    $[21] = recipient;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const handleTransfer = t10;
  let t11;
  if ($[23] !== onClose) {
    t11 = () => {
      setIsVisible(false);
      setTimeout(() => onClose(), 300);
    };
    $[23] = onClose;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleClose = t11;
  let t12;
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      requestAnimationFrame(() => setIsVisible(true));
    };
    t13 = [];
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[27] !== isVisible) {
    t14 = () => {
      if (!isVisible) {
        return;
      }
      const html = document.documentElement;
      const original = html.style.overflow;
      html.style.overflow = "hidden";
      return () => {
        html.style.overflow = original;
      };
    };
    t15 = [isVisible];
    $[27] = isVisible;
    $[28] = t14;
    $[29] = t15;
  } else {
    t14 = $[28];
    t15 = $[29];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[30] !== search) {
    t16 = () => {
      const searchValue = search.trim().replace(/^@+/, "");
      if (!searchValue) {
        return;
      }
      const timer = setTimeout(() => setDebouncedSearch(searchValue), 1500);
      return () => clearTimeout(timer);
    };
    t17 = [search];
    $[30] = search;
    $[31] = t16;
    $[32] = t17;
  } else {
    t16 = $[31];
    t17 = $[32];
  }
  useEffect(t16, t17);
  let t18;
  if ($[33] !== reset || $[34] !== walletsResult.data?.wallets) {
    t18 = () => {
      const fresh = walletsResult.data?.wallets;
      if (fresh) {
        reset(fresh);
      }
    };
    $[33] = reset;
    $[34] = walletsResult.data?.wallets;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = walletsResult.data?.wallets;
  let t20;
  if ($[36] !== debouncedSearch || $[37] !== reset || $[38] !== t19) {
    t20 = [t19, debouncedSearch, reset];
    $[36] = debouncedSearch;
    $[37] = reset;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  useEffect(t18, t20);
  let t21;
  if ($[40] !== handleClose || $[41] !== language || $[42] !== transferResult.data || $[43] !== transferResult.error) {
    t21 = () => {
      if (!transferResult.error && transferResult.data) {
        const tx = transferResult.data.transfer;
        const message = `UUID: ${tx.uuid}\n\nSender: ${tx.sender?.uuid}\n\nRecipient: ${tx.recipient?.uuid}\n\nAmount: ${formatAmount(tx.amount, language)} FTH\n\nTimestamp: ${new Date(tx.timestamp as string).toLocaleString(language ?? "en")}`;
        if (hapticFeedback.isSupported()) {
          hapticFeedback.notificationOccurred("success");
        }
        if (popup.isSupported()) {
          popup.show({
            title: "Transaction Accepted",
            message,
            buttons: [{
              type: "ok"
            }]
          });
        }
        copyTextToClipboard(message);
        handleClose();
      }
    };
    $[40] = handleClose;
    $[41] = language;
    $[42] = transferResult.data;
    $[43] = transferResult.error;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== handleClose || $[46] !== language || $[47] !== transferResult) {
    t22 = [handleClose, language, transferResult];
    $[45] = handleClose;
    $[46] = language;
    $[47] = transferResult;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  useEffect(t21, t22);
  const t23 = isVisible ? "opacity-100" : "opacity-0";
  let t24;
  if ($[49] !== t23) {
    t24 = clsx("fixed inset-0 backdrop-blur-lg z-100 flex items-end transition-opacity duration-300 ease-out", t23);
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  const t25 = isVisible ? "translate-y-0" : "translate-y-full";
  let t26;
  if ($[51] !== t25) {
    t26 = clsx("max-h-[85vh] bg-surface w-full rounded-t-4xl overflow-auto transition-transform duration-300 ease-out", t25);
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <h2 className="text-2xl font-semibold">Transfer Feathers</h2>;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== handleClose) {
    t28 = <div className="p-6 border-b flex justify-between items-center">{t27}<Image src="/icon-close.svg" alt="Close" width={32} height={32} onClick={handleClose} className="cursor-pointer" /></div>;
    $[54] = handleClose;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <p className="text-sm text-muted mb-3">Recipient</p>;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  const t30 = recipient || search;
  let t31;
  let t32;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = e_0 => {
      setRecipient("");
      setSearch(e_0.target.value);
    };
    t32 = () => setTimeout(() => recipientInputRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    }), 150);
    $[57] = t31;
    $[58] = t32;
  } else {
    t31 = $[57];
    t32 = $[58];
  }
  const t33 = isOwnRecipient ? "border-error" : "border-secondary/30";
  let t34;
  if ($[59] !== t33) {
    t34 = clsx("w-full bg-background border rounded-3xl px-5 py-4 text-lg", t33);
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t30 || $[62] !== t34) {
    t35 = <div>{t29}<input ref={recipientInputRef} type="text" placeholder="UUID or Username" value={t30} onChange={t31} onFocus={t32} className={t34} /></div>;
    $[61] = t30;
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== debouncedSearch || $[65] !== hasMore || $[66] !== isLoadingMore || $[67] !== scrollRef || $[68] !== sentinelRef || $[69] !== wallets) {
    t36 = debouncedSearch && (wallets?.length ?? 0) > 0 && <div ref={scrollRef} className="overflow-auto bg-background rounded-3xl p-3 space-y-3 border border-secondary/20 max-h-[40vh]">{wallets.map(wallet_0 => {
        const bird = wallet_0.bird;
        return <div key={wallet_0.uuid} onClick={() => handleSelectWallet(wallet_0)} className="flex items-center gap-4 bg-surface rounded-3xl p-4 cursor-pointer active:scale-90 transition-transform">{bird ? <><div className="relative size-14 rounded-full overflow-hidden border border-primary/30 shrink-0">{bird.premium && <div className="absolute inset-0 z-150 rounded-full bg-linear-to-b from-transparent to-gold/50 animate-pulse" />}<Image src={`/avatars/${bird.avatar?.toLowerCase()}.png`} alt={bird.username} fill={true} className="object-contain p-2 z-175" /></div><div className="flex-1 min-w-0"><p className="text-sm truncate">{bird.firstName} {bird.lastName}</p><p className="text-muted truncate">@{bird.username}</p><div className={clsx("mt-2 py-2 text-xs text-center font-semibold rounded-full whitespace-nowrap", leagueBadgeClasses[bird.league])}>{bird.league}</div></div></> : <div className="flex-1 min-w-0 flex items-center"><div><p className="text-sm">Wallet</p><p className="text-xs text-muted truncate">{wallet_0.uuid}</p></div></div>}<div className="text-right shrink-0"><div className="flex items-center gap-1 justify-end"><span className="text-2xl font-bold">{wallet_0.balance}</span><span className="text-xl text-primary">FTH</span></div></div></div>;
      })}{isLoadingMore && <div className="flex justify-center py-6"><span className="text-muted animate-pulse">Loading more recipients...</span></div>}{hasMore && <div ref={sentinelRef} className="h-6" />}</div>;
    $[64] = debouncedSearch;
    $[65] = hasMore;
    $[66] = isLoadingMore;
    $[67] = scrollRef;
    $[68] = sentinelRef;
    $[69] = wallets;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <p className="text-sm text-muted mb-3">Amount</p>;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  let t39;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = e_1 => setAmount(Number(e_1.target.value));
    t39 = () => setTimeout(() => amountInputRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    }), 150);
    $[72] = t38;
    $[73] = t39;
  } else {
    t38 = $[72];
    t39 = $[73];
  }
  const t40 = isInvalidAmount ? "border-error" : "border-secondary/30";
  let t41;
  if ($[74] !== t40) {
    t41 = clsx("w-full bg-background border rounded-3xl px-5 py-4 text-lg text-center", t40);
    $[74] = t40;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== amount || $[77] !== balance || $[78] !== t41) {
    t42 = <div>{t37}<input ref={amountInputRef} type="number" value={amount} max={balance} onChange={t38} onFocus={t39} className={t41} /></div>;
    $[76] = amount;
    $[77] = balance;
    $[78] = t41;
    $[79] = t42;
  } else {
    t42 = $[79];
  }
  const t43 = transferResult.fetching && "animate-pulse";
  let t44;
  if ($[80] !== t43) {
    t44 = clsx("w-full rounded-full bg-linear-to-r from-primary to-secondary py-6 text-2xl font-semibold", t43);
    $[80] = t43;
    $[81] = t44;
  } else {
    t44 = $[81];
  }
  let t45;
  if ($[82] !== amount || $[83] !== language) {
    t45 = formatAmount(amount, language);
    $[82] = amount;
    $[83] = language;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== handleTransfer || $[86] !== t44 || $[87] !== t45) {
    t46 = <button onClick={handleTransfer} className={t44}>Send {t45} FTH</button>;
    $[85] = handleTransfer;
    $[86] = t44;
    $[87] = t45;
    $[88] = t46;
  } else {
    t46 = $[88];
  }
  let t47;
  if ($[89] !== t35 || $[90] !== t36 || $[91] !== t42 || $[92] !== t46) {
    t47 = <div className="p-6 space-y-8">{t35}{t36}{t42}{t46}</div>;
    $[89] = t35;
    $[90] = t36;
    $[91] = t42;
    $[92] = t46;
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] !== t26 || $[95] !== t28 || $[96] !== t47) {
    t48 = <div className={t26} onClick={_temp2}>{t28}{t47}</div>;
    $[94] = t26;
    $[95] = t28;
    $[96] = t47;
    $[97] = t48;
  } else {
    t48 = $[97];
  }
  let t49;
  if ($[98] !== handleClose || $[99] !== t24 || $[100] !== t48) {
    t49 = <div className={t24} onClick={handleClose}>{t48}</div>;
    $[98] = handleClose;
    $[99] = t24;
    $[100] = t48;
    $[101] = t49;
  } else {
    t49 = $[101];
  }
  return t49;
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp(data) {
  return data?.wallets ?? [];
}